/**
 * Module: hooks/useFileUpload
 * Role: React state wrapper around _uploadXHR (Pantene XHR helper). Exposes
 *       progress 0..1, status, abort. Each call to `start` aborts any prior
 *       in-flight upload and releases the previous File reference for GC.
 *
 * Uses: screens/pantene/_uploadXHR:uploadXHR, screens/pantene/_uploadXHR:UploadProgress
 * Used by: screens/flow/templates/UgcPhotoGridScreen
 *
 * Notes:
 *   - Memory: hook keeps NO reference to `file` after `start` resolves —
 *     caller (grid) drops its own ref between iterations so 5 iPhone photos
 *     never live in memory simultaneously.
 *   - DOMException AbortError is mapped to status:'aborted', not 'error'.
 *   - `start` re-throws on error/abort so callers can halt sequential loops.
 *
 * ADR-023: NO window.Telegram access, no platform string literals.
 * ADR-022: size-limit constant lives in shared/limits.ts.
 *
 * Glossary: ai/glossary/buyer.md
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { uploadXHR, type UploadProgress } from '../screens/pantene/_uploadXHR';

// ─── Types ──────────────────────────────────────────────────────────────────

export type UploadStatus = 'idle' | 'uploading' | 'success' | 'error' | 'aborted';

export interface UseFileUploadResult {
  /** null while idle; 0..1 ratio during upload; 1 on success. */
  progress: number | null;
  status: UploadStatus;
  /** Error object (never string). Set only when status === 'error'. */
  error: Error | null;
  /**
   * Start uploading `file` to `url` via HTTP PUT.
   * Re-throws on upload error or abort so caller can break a sequential loop.
   */
  start: (args: { url: string; file: File }) => Promise<void>;
  /** Abort an in-flight upload. Sets status to 'aborted'. */
  abort: () => void;
  /** Reset all state back to idle. progress becomes null. */
  reset: () => void;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useFileUpload(): UseFileUploadResult {
  const [progress, setProgress] = useState<number | null>(null);
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [error, setError] = useState<Error | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  // Cleanup any in-flight upload on unmount.
  useEffect(() => () => {
    abortRef.current?.abort();
  }, []);

  const abort = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setProgress(null);
    setStatus('idle');
    setError(null);
  }, []);

  const start = useCallback(async ({ url, file }: { url: string; file: File }) => {
    // Abort any previous in-flight upload before starting a new one.
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setStatus('uploading');
    setProgress(0);
    setError(null);

    try {
      await uploadXHR({
        url,
        file,
        signal: ctrl.signal,
        onProgress: (p: UploadProgress) => {
          const ratio = p.total > 0 ? p.loaded / p.total : 0;
          setProgress(ratio);
        },
      });
      setStatus('success');
      setProgress(1);
    } catch (err) {
      const isAbort =
        err instanceof DOMException && err.name === 'AbortError';
      if (isAbort) {
        setStatus('aborted');
      } else {
        setError(err instanceof Error ? err : new Error(String(err)));
        setStatus('error');
      }
      // Re-throw so caller can decide flow control (e.g. break sequential loop).
      throw err;
    } finally {
      // Drop AbortController reference once we leave the in-flight window.
      if (abortRef.current === ctrl) abortRef.current = null;
    }
  }, []);

  return { progress, status, error, start, abort, reset };
}
